import {
  buildPblCvChoiceTextValues,
  inferPblLanguageOtherText,
  inferPblProfessionalQualificationOtherText,
  normalizePblLanguageValues,
  normalizePblLawFirmPositionValue,
  normalizePblProfessionalQualificationValues,
  normalizePblServiceAreaValues,
  type PblLanguageValue,
  type PblLawFirmPositionValue,
  type PblProfessionalQualificationValue,
} from '@/components/Pbl/VolunteerRegistration/profileCvValues';
import type { PblLawyerProfile, PblServiceArea } from '@/types/pbl';

export type LawyerCvTextFieldName =
  | 'full_name_en'
  | 'full_name_zh'
  | 'law_firm_name'
  | 'law_firm_name_zh'
  | 'cv_full_name_en'
  | 'cv_full_name_zh'
  | 'cv_law_firm_en'
  | 'cv_law_firm_zh'
  | 'cv_professional_experience_en'
  | 'cv_professional_experience_zh'
  | 'cv_pro_bono_experience_en'
  | 'cv_pro_bono_experience_zh';

type LawyerCvTextValues = Pick<Partial<PblLawyerProfile>, LawyerCvTextFieldName>;

export type LawyerCvFormValues = LawyerCvTextValues & {
  cv_publication_consent: boolean;
  firm_authorisation_file_id?: number | null;
  firm_authorisation_confirmed: boolean;
  firm_authorisation_letter_attached: boolean;
  law_firm_change_notify_agreed: boolean;
  position_in_law_firm: PblLawFirmPositionValue | '';
  position_other_text: string;
  hk_solicitor_admission_year: number | '';
  other_professional_qualifications: PblProfessionalQualificationValue[];
  other_professional_qualification_text: string;
  service_areas: PblServiceArea[];
  language_proficiency: PblLanguageValue[];
  language_other_text: string;
};

const textFieldNames: LawyerCvTextFieldName[] = [
  'full_name_en',
  'full_name_zh',
  'law_firm_name',
  'law_firm_name_zh',
  'cv_full_name_en',
  'cv_full_name_zh',
  'cv_law_firm_en',
  'cv_law_firm_zh',
  'cv_professional_experience_en',
  'cv_professional_experience_zh',
  'cv_pro_bono_experience_en',
  'cv_pro_bono_experience_zh',
];

export const defaultLawyerCvFormValues: LawyerCvFormValues = {
  cv_publication_consent: false,
  firm_authorisation_file_id: null,
  firm_authorisation_confirmed: false,
  firm_authorisation_letter_attached: false,
  law_firm_change_notify_agreed: false,
  full_name_en: '',
  full_name_zh: '',
  law_firm_name: '',
  law_firm_name_zh: '',
  position_in_law_firm: '',
  position_other_text: '',
  cv_full_name_en: '',
  cv_full_name_zh: '',
  cv_law_firm_en: '',
  cv_law_firm_zh: '',
  cv_professional_experience_en: '',
  cv_professional_experience_zh: '',
  cv_pro_bono_experience_en: '',
  cv_pro_bono_experience_zh: '',
  hk_solicitor_admission_year: '',
  other_professional_qualifications: [],
  other_professional_qualification_text: '',
  service_areas: [],
  language_proficiency: [],
  language_other_text: '',
};

const hasText = (value: unknown): value is string => typeof value === 'string' && Boolean(value.trim());
const firstText = (...values: unknown[]) => values.find(hasText)?.trim() || '';

const fallbackFields: Array<keyof PblLawyerProfile> = [
  'position_other_text',
  'other_professional_qualification_text',
  'language_other_text',
  'cv_position_en',
  'cv_position_zh',
  'cv_other_qualifications_en',
  'cv_other_qualifications_zh',
  'cv_languages_en',
  'cv_languages_zh',
  'cv_primary_practice_areas_en',
  'cv_primary_practice_areas_zh',
];

export const mergeLawyerCvWorkspacePayload = (
  published: Partial<PblLawyerProfile> = {},
  editable: Partial<PblLawyerProfile> = {}
) => {
  const merged: Partial<PblLawyerProfile> = { ...published, ...editable };

  fallbackFields.forEach((field) => {
    if (!hasText(merged[field]) && hasText(published[field])) {
      (merged as Record<string, unknown>)[field] = published[field];
    }
  });

  return merged;
};

export const normalizeLawyerCvFormValues = (source: Partial<PblLawyerProfile>): LawyerCvFormValues => {
  const textValues = Object.fromEntries(
    textFieldNames.map((name) => {
      const profileFallback =
        name === 'cv_full_name_en'
          ? source.full_name_en
          : name === 'cv_full_name_zh'
          ? source.full_name_zh
          : name === 'cv_law_firm_en'
          ? source.law_firm_name
          : name === 'cv_law_firm_zh'
          ? source.law_firm_name_zh
          : '';
      return [name, String(source[name] ?? profileFallback ?? '')];
    })
  ) as LawyerCvTextValues;
  const professionalQualifications = normalizePblProfessionalQualificationValues(
    source.other_professional_qualifications
  );
  const languages = normalizePblLanguageValues(source.language_proficiency);
  const position = normalizePblLawFirmPositionValue(source.position_in_law_firm);

  return {
    ...defaultLawyerCvFormValues,
    ...textValues,
    cv_publication_consent: Boolean(source.cv_publication_consent),
    firm_authorisation_file_id: source.firm_authorisation_file_id ?? null,
    firm_authorisation_confirmed: Boolean(source.firm_authorisation_confirmed),
    firm_authorisation_letter_attached: Boolean(source.firm_authorisation_letter_attached),
    law_firm_change_notify_agreed: Boolean(source.law_firm_change_notify_agreed),
    position_in_law_firm: position,
    position_other_text: firstText(source.position_other_text, position === 'other' ? source.cv_position_en : ''),
    hk_solicitor_admission_year: source.hk_solicitor_admission_year ?? '',
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: firstText(
      source.other_professional_qualification_text,
      inferPblProfessionalQualificationOtherText(source.cv_other_qualifications_en),
      inferPblProfessionalQualificationOtherText(source.cv_other_qualifications_zh)
    ),
    service_areas: normalizePblServiceAreaValues(source.service_areas),
    language_proficiency: languages,
    language_other_text: firstText(
      source.language_other_text,
      inferPblLanguageOtherText(source.cv_languages_en),
      inferPblLanguageOtherText(source.cv_languages_zh)
    ),
  };
};

const sameSelections = (first: string[], second: string[]) =>
  [...first].sort().join('|') === [...second].sort().join('|');

const sameText = (first: string, second: string) => first.trim() === second.trim();

const preservedChoiceText = (
  baseline: Partial<PblLawyerProfile>,
  field: keyof PblLawyerProfile,
  generatedValue: string,
  changed: boolean
) => (!changed && hasText(baseline[field]) ? String(baseline[field]) : generatedValue);

export const buildLawyerCvPayload = (
  values: LawyerCvFormValues,
  baseline: Partial<PblLawyerProfile> = {}
): Partial<PblLawyerProfile> => {
  const {
    hk_solicitor_admission_year: admissionYear,
    position_in_law_firm: positionInLawFirm,
    position_other_text: positionOtherText,
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: professionalQualificationOtherText,
    service_areas: serviceAreas,
    language_proficiency: languageProficiency,
    language_other_text: languageOtherText,
    ...textValues
  } = values;
  const baselineValues = normalizeLawyerCvFormValues(baseline);
  const generatedValues = buildPblCvChoiceTextValues({
    position_in_law_firm: positionInLawFirm,
    position_other_text: positionOtherText,
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: professionalQualificationOtherText,
    service_areas: serviceAreas,
    language_proficiency: languageProficiency,
    language_other_text: languageOtherText,
  });
  const admissionYearText = admissionYear === '' ? '' : String(admissionYear);
  const positionChanged =
    positionInLawFirm !== baselineValues.position_in_law_firm ||
    !sameText(positionOtherText, baselineValues.position_other_text);
  const qualificationsChanged =
    !sameSelections(professionalQualifications, baselineValues.other_professional_qualifications) ||
    !sameText(professionalQualificationOtherText, baselineValues.other_professional_qualification_text);
  const languagesChanged =
    !sameSelections(languageProficiency, baselineValues.language_proficiency) ||
    !sameText(languageOtherText, baselineValues.language_other_text);
  const serviceAreasChanged = !sameSelections(serviceAreas, baselineValues.service_areas);

  return {
    ...textValues,
    position_in_law_firm: positionInLawFirm,
    position_other_text: positionInLawFirm === 'other' ? positionOtherText : '',
    other_professional_qualifications: professionalQualifications,
    other_professional_qualification_text: professionalQualifications.includes('other')
      ? professionalQualificationOtherText
      : '',
    ...(admissionYear === '' ? {} : { hk_solicitor_admission_year: Number(admissionYear) }),
    cv_admission_year_en: admissionYearText,
    cv_admission_year_zh: admissionYearText,
    service_areas: serviceAreas,
    language_proficiency: languageProficiency,
    language_other_text: languageProficiency.includes('other') ? languageOtherText : '',
    cv_position_en: preservedChoiceText(baseline, 'cv_position_en', generatedValues.cv_position_en, positionChanged),
    cv_position_zh: preservedChoiceText(baseline, 'cv_position_zh', generatedValues.cv_position_zh, positionChanged),
    cv_other_qualifications_en: preservedChoiceText(
      baseline,
      'cv_other_qualifications_en',
      generatedValues.cv_other_qualifications_en,
      qualificationsChanged
    ),
    cv_other_qualifications_zh: preservedChoiceText(
      baseline,
      'cv_other_qualifications_zh',
      generatedValues.cv_other_qualifications_zh,
      qualificationsChanged
    ),
    cv_languages_en: preservedChoiceText(
      baseline,
      'cv_languages_en',
      generatedValues.cv_languages_en,
      languagesChanged
    ),
    cv_languages_zh: preservedChoiceText(
      baseline,
      'cv_languages_zh',
      generatedValues.cv_languages_zh,
      languagesChanged
    ),
    cv_primary_practice_areas_en: preservedChoiceText(
      baseline,
      'cv_primary_practice_areas_en',
      generatedValues.cv_primary_practice_areas_en,
      serviceAreasChanged
    ),
    cv_primary_practice_areas_zh: preservedChoiceText(
      baseline,
      'cv_primary_practice_areas_zh',
      generatedValues.cv_primary_practice_areas_zh,
      serviceAreasChanged
    ),
    cv_publication_consent: values.cv_publication_consent,
  };
};
